// Theme definitions for town and neighborhood pages
// These themes ONLY apply to public-facing town/area pages

export interface ThemeColors {
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  background: string;
  foreground: string;
  accent: string;
  accentForeground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  border: string;
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  preview: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

export const themes: Record<string, Theme> = {
  classic: {
    id: 'classic',
    name: 'Classic Real Estate',
    description: 'Traditional brokerage style with navy and slate tones',
    colors: {
      primary: '222 47% 20%',           // Navy
      primaryForeground: '60 9% 98%',
      secondary: '215 25% 45%',          // Slate blue
      secondaryForeground: '60 9% 98%',
      background: '60 9% 98%',           // Off-white
      foreground: '220 15% 20%',         // Dark gray
      accent: '215 35% 50%',             // Muted blue
      accentForeground: '60 9% 98%',
      muted: '220 14% 96%',
      mutedForeground: '220 9% 46%',
      card: '0 0% 100%',
      cardForeground: '220 15% 20%',
      border: '220 13% 91%',
    },
    preview: {
      primary: '#1e2a3a',
      secondary: '#5a7a9a',
      accent: '#4a7ab0',
      background: '#fafaf8',
    },
  },
  modern: {
    id: 'modern',
    name: 'Modern SaaS',
    description: 'Clean, tech-forward look inspired by Salesforce',
    colors: {
      primary: '210 100% 50%',           // Bright blue
      primaryForeground: '0 0% 100%',
      secondary: '220 70% 25%',          // Dark blue
      secondaryForeground: '0 0% 100%',
      background: '0 0% 100%',           // White
      foreground: '220 20% 20%',
      accent: '210 100% 45%',            // Blue CTA
      accentForeground: '0 0% 100%',
      muted: '220 14% 96%',              // Light gray panels
      mutedForeground: '220 9% 46%',
      card: '0 0% 100%',
      cardForeground: '220 20% 20%',
      border: '220 13% 90%',
    },
    preview: {
      primary: '#0080ff',
      secondary: '#1a365d',
      accent: '#0073e6',
      background: '#ffffff',
    },
  },
  luxury: {
    id: 'luxury',
    name: 'Luxury Real Estate',
    description: 'Understated and premium with charcoal and gold accents',
    colors: {
      primary: '220 10% 20%',            // Charcoal
      primaryForeground: '45 30% 96%',
      secondary: '30 5% 50%',            // Warm gray
      secondaryForeground: '45 30% 96%',
      background: '45 30% 96%',          // Cream
      foreground: '220 10% 20%',
      accent: '45 50% 55%',              // Soft gold
      accentForeground: '220 10% 15%',
      muted: '45 20% 92%',
      mutedForeground: '220 10% 40%',
      card: '45 40% 98%',
      cardForeground: '220 10% 20%',
      border: '45 15% 85%',
    },
    preview: {
      primary: '#2d2f33',
      secondary: '#8a8580',
      accent: '#c9a857',
      background: '#f8f6f0',
    },
  },
  coastal: {
    id: 'coastal',
    name: 'Coastal New England',
    description: 'Local and regional tone with navy and teal accents',
    colors: {
      primary: '220 50% 20%',            // Deep navy
      primaryForeground: '0 0% 100%',
      secondary: '210 40% 40%',          // Harbor blue
      secondaryForeground: '0 0% 100%',
      background: '210 20% 98%',         // Light gray/white
      foreground: '220 30% 20%',
      accent: '175 35% 40%',             // Muted teal
      accentForeground: '0 0% 100%',
      muted: '210 15% 95%',
      mutedForeground: '210 10% 45%',
      card: '0 0% 100%',
      cardForeground: '220 30% 20%',
      border: '210 15% 88%',
    },
    preview: {
      primary: '#1a2a40',
      secondary: '#3d6a8a',
      accent: '#4a8a80',
      background: '#f7f9fa',
    },
  },
  light: {
    id: 'light',
    name: 'Light & Airy',
    description: 'Clean white header with soft gray accents',
    colors: {
      primary: '0 0% 100%',              // White header
      primaryForeground: '220 20% 25%',  // Dark text on white
      secondary: '220 14% 96%',          // Light gray
      secondaryForeground: '220 20% 25%',
      background: '0 0% 100%',           // White
      foreground: '220 20% 20%',         // Dark gray text
      accent: '210 80% 50%',             // Bright blue accent
      accentForeground: '0 0% 100%',
      muted: '220 14% 96%',              // Light gray
      mutedForeground: '220 9% 46%',
      card: '0 0% 100%',
      cardForeground: '220 20% 20%',
      border: '220 13% 91%',
    },
    preview: {
      primary: '#ffffff',
      secondary: '#f4f4f5',
      accent: '#1a8cff',
      background: '#ffffff',
    },
  },
};

export const themeList = Object.values(themes);

export function getTheme(themeId: string): Theme {
  return themes[themeId] || themes.classic;
}
